/* eslint-disable no-restricted-globals */
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import TmpEmployee from './tmp/employee';
import TmpObject from './tmp/object';
import TmpCategory from './tmp/category';
import ApplicationUser from './user/index';
import { DialogInputField, dialogInputStore } from './dialogs/input/dialogInputStore';
import { MatenRequestResponse } from '../shared';
import { setPrimaryColor, isEmbedded, createFingerprint, generateDeviceId } from '../utils/helper';
import { MATEN_LOGIN_PREFIX, MATEN_ACCESS_PREFIX } from './constants';

export interface ClientAuth {
    token: string;
    tabId: string;
}

const wait = (ms: number) =>
    new Promise((res, rej) => setTimeout(() => rej(new Error(`timed out after ${ms} ms`)), ms));

class ApplicationSdk {
    public user: ApplicationUser | null = null;
    public tabId: string | null = null;
    public deviceId: string | null = null;
    public fingerPrint: string = createFingerprint();

    public location: string = '/';
    public locationPublic: boolean = false;
    public locationPublicUrl: string | null = null;

    public locationEntity: string | null = null;
    public locationAction: string | null = null;
    public locationParam: string | null = null; // For iframe menu keys
    public adminMode: boolean = false;

    public customError: any = null;
    public currentStore: any = null;
    public currentDialog: any = null;

    public toastCb: any = null;

    public employee: TmpEmployee | null = null;
    public objects: TmpObject;
    public categories: TmpCategory;

    public appConfig: any = null;
    public appEmbedded: boolean = isEmbedded();

    constructor() {
        makeObservable(this, {
            // currentUser: observable,
            adminMode: observable,
            currentStore: observable,
            currentDialog: observable,
            location: observable,
            locationEntity: observable,
            locationAction: observable,
            locationParam: observable,
            user: observable,
            employee: observable,
            objects: observable,
            categories: observable,
            customError: observable,
            appConfig: observable,
            routeTo: action,
            init: action,
            useStore: action,
            closeDialog: action,
            openDialog: action,
            toggleAdminMode: action,
            menuData: computed,
        });

        this.customError = null;

        this.employee = new TmpEmployee(this);
        this.objects = new TmpObject(this);
        this.categories = new TmpCategory(this);

        // @ts-ignore
        window.sdk = this;
    }

    public routeTo(path: string): void {
        history.pushState({}, '', path);
        this.location = path;
        this.locationEntity = this.location.split('/')?.[1] || null;
        this.locationAction = this.location.split('/')?.[2] || null;
        this.locationParam = this.location.split('/')?.[3] || null;
        this.locationParam = this.location.split('/')?.[3] || null; // For iframe menu keys
    }

    public toggleAdminMode(): void {
        if (this.user?.isAdmin) {
            this.adminMode = !this.adminMode;

            // Route to appropriate section based on mode
            if (this.adminMode) {
                this.routeTo('/admin/system');
            } else {
                // this.routeTo('/manager/orders');
                this.refreshAppData();
                this.routeToManager();
            }
        }
    }

    public async handleAppData(appConfig: any): Promise<void> {
        if (!appConfig) {
            return;
        }

        runInAction(() => {
            this.appConfig = appConfig;
        });

        setPrimaryColor(this.appConfig.design.primaryColor);
    }
    /**
     *
     * @returns Promise<void>
     * Initializes the SDK, sets up user and app configuration.
     * Handles login via URL token or session storage.
     */
    public async init(): Promise<void> {
        const location: string = window.location?.pathname || '/';
        this.locationPublic = location.startsWith('/public');

        // public route, do not init anything
        if (this.locationPublic) {
            this.locationPublicUrl = location.replace('/public', '') || '/';
            return;
        }

        const locEntity = location.split('/')?.[1] || null;
        const locAction = location.split('/')?.[2] || null;

        // const path: string | undefined = window?.location?.pathname;
        const searchData: string | undefined = window?.location?.search;
        const searchParams: URLSearchParams = new URLSearchParams(searchData);

        let clientAuth: ClientAuth | null = null;
        let appConfig: any = null;

        this.deviceId = localStorage.getItem('maten-device-id') || null;

        if (this.deviceId === null) {
            this.deviceId = generateDeviceId();
            localStorage.setItem('maten-device-id', this.deviceId);
        }

        this.tabId = sessionStorage.getItem('maten-tab-id') || null;

        // We have different login methods
        if (locEntity === 'auth' && locAction === 'login') {
            let useToken: string = searchParams.get('token') || '';
            // if we do not have a token return to error page
            if (!useToken) {
                this.routeToError();
                return;
            }

            // Make sure that it is prefixed
            if (!useToken.startsWith(MATEN_ACCESS_PREFIX)) {
                useToken = `${MATEN_LOGIN_PREFIX}_${useToken}`;
            }

            const fetchLoginRequest = await this.fetchApi('auth', 'login', {
                token: useToken,
            });

            clientAuth = fetchLoginRequest?.json?.auth || null;
            appConfig = fetchLoginRequest?.json?.app || null;

            // If there is no auth contained, we cannot continue
            if (!clientAuth) {
                this.routeToError();
                return;
            }
        } else {
            // try a relogin
            const fetchReLoginRequest = await this.fetchApi('auth', 'relogin');

            clientAuth = fetchReLoginRequest?.json?.auth || null;
            appConfig = fetchReLoginRequest?.json?.app || null;

            // If there is no auth contained, we cannot continue
            if (!clientAuth) {
                this.routeToError();
                return;
            }
        }

        this.handleAppData(appConfig);

        if (clientAuth.tabId) {
            this.tabId = clientAuth.tabId;
            sessionStorage.setItem('maten-tab-id', this.tabId);
        }

        runInAction(() => {
            this.user = new ApplicationUser(clientAuth);
        });

        // Route the user to the right place
        if (this.user?.isManager) {
            this.routeToManager();
        } else if (this.user?.isEmployee) {
            this.routeToEmployee(appConfig.view);
        } else {
            this.routeToError();
        }

        // reset search Params
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    public async refreshAppData(): Promise<void> {
        const fetchAppData = await this.fetchApi('auth', 'app/refresh');

        const appConfig: any = fetchAppData?.json?.app || null;

        if (appConfig) {
            this.handleAppData(appConfig);
        }
    }

    public async uploadImage(
        entity: string,
        action: string,
        image: any,
        options: any = {
            timeout: 10000,
            additionalData: null,
        }
    ): Promise<any> {
        const useUrl = `${process.env.REACT_APP_BACKEND_API}${entity}/${action}`;
        const formData = new FormData();
        formData.append('file', image);

        if (options?.additionalData) {
            Object.keys(options.additionalData).forEach(key => {
                if (typeof options.additionalData[key] === 'object') {
                    formData.append(key, JSON.stringify(options.additionalData[key]));
                } else {
                    formData.append(key, options.additionalData[key]);
                }
            });
        }

        return new Promise((resolve, reject) => {
            const abortController = new AbortController();
            const fetchData = fetch(useUrl, {
                method: 'POST',
                signal: abortController.signal,
                // @ts-ignore
                headers: {
                    Authorization: `Bearer ${this.user?.userToken || ''}`,
                    'X-Tab-Id': this.currentTabId,
                    'X-Fingerprint': this.fingerPrint,
                    'X-Device-Id': this.currentDeviceId,
                },
                body: formData,
            });

            const fetchOrTimeout = Promise.race([fetchData, wait(options.timeout)]);

            fetchOrTimeout
                .then(async (response: any) => {
                    // if (useSpinner) useSpinner.close();
                    let jsonResult = null;
                    try {
                        jsonResult = await response?.json();
                    } catch (e) {}
                    resolve({
                        json: jsonResult || null,
                        status: response.status,
                    });
                })
                .catch((error: any) => {
                    // if (useSpinner) useSpinner.close();
                    abortController.abort();
                    resolve(undefined);
                });
        });
    }

    public async uploadPdf(
        entity: string,
        action: string,
        pdfFile: any,
        options: any = {
            timeout: 10000,
            additionalData: null,
        }
    ): Promise<any> {
        const useUrl = `${process.env.REACT_APP_BACKEND_API}${entity}/${action}`;

        const formData = new FormData();
        formData.append('file', pdfFile);

        if (options?.additionalData) {
            Object.keys(options.additionalData).forEach(key => {
                if (typeof options.additionalData[key] === 'object') {
                    formData.append(key, JSON.stringify(options.additionalData[key]));
                } else {
                    formData.append(key, options.additionalData[key]);
                }
            });
        }

        return new Promise((resolve, reject) => {
            const abortController = new AbortController();
            const fetchData = fetch(useUrl, {
                method: 'POST',
                signal: abortController.signal,
                // @ts-ignore
                headers: {
                    Authorization: `Bearer ${this.user?.userToken || ''}`,
                    'X-Tab-Id': this.currentTabId,
                    'X-Fingerprint': this.fingerPrint,
                    'X-Device-Id': this.currentDeviceId,
                },
                body: formData,
            });

            const fetchOrTimeout = Promise.race([fetchData, wait(options.timeout)]);

            fetchOrTimeout
                .then(async (response: any) => {
                    let jsonResult = null;
                    try {
                        jsonResult = await response?.json();
                    } catch (e) {}
                    resolve({
                        json: jsonResult || null,
                        status: response.status,
                    });
                })
                .catch((error: any) => {
                    abortController.abort();
                    resolve(undefined);
                });
        });
    }

    public async fetchApi(
        entity: string,
        action: string,
        data: any = {},
        options: any = {
            timeout: 10000,
            spinner: true,
            multiPart: false,
        }
    ): Promise<any> {
        const fetchResult = await this.fetchApiData(entity, action, data, options);

        if (fetchResult?.xToken) {
            this.user?.updateToken(fetchResult.xToken);
        }
        if (fetchResult?.status === 401) {
            runInAction(() => {
                this.customError = {
                    customMessage: fetchResult?.json?.customMessage || null,
                    code: fetchResult?.json?.error || null,
                };
            });
            this.routeToError();
        }

        return fetchResult;
    }

    public routeToEmployee(task: string): void {
        const find: any = this.menuData.find((item: any) => item.key === task);
        if (find) {
            if (find.type === 'iframe') {
                this.routeTo(`/employee/menu`);
                return;
            }
            this.routeTo(`/employee/${task}`);
        } else {
            this.routeTo(`/employee/menu`);
        }
    }

    public routeToManager(): void {
        // Route to default page
        let defaultPage =
            this.filteredMenuData.sort((a: any, b: any) => b.prio - a.prio)?.[0] || null;
        this.routeTo(defaultPage?.path || '/manager/orders');
    }

    public routeToError(): void {
        localStorage.removeItem('token');
        this.routeTo('/auth/error');
    }

    public async fetchApiData(
        entity: string,
        action: string,
        data: any = {},
        options: any = {
            timeout: 10000,
            spinner: true,
            multiPart: false,
        }
    ): Promise<any> {
        const useUrl = `${process.env.REACT_APP_BACKEND_API}${entity}/${action}`;

        return new Promise((resolve, reject) => {
            const abortController = new AbortController();
            const fetchData = fetch(useUrl, {
                method: 'POST',
                signal: abortController.signal,
                credentials: 'include', // Include cookies in requests
                // @ts-ignore
                headers: {
                    'Content-Type': options?.multiPart ? 'multipart/form-data' : 'application/json',
                    Authorization: `Bearer ${this.user?.userToken || ''}`,
                    'X-Tab-Id': this.currentTabId,
                    'X-Fingerprint': this.fingerPrint,
                    'X-Device-Id': this.currentDeviceId,
                },
                body: JSON.stringify(data || {}),
            });

            const fetchOrTimeout = Promise.race([fetchData, wait(options.timeout)]);

            fetchOrTimeout
                .then(async (response: any) => {
                    // if (useSpinner) useSpinner.close();
                    let jsonResult = null;
                    try {
                        jsonResult = await response?.json();
                    } catch (e) {}

                    resolve({
                        json: jsonResult || null,
                        status: response.status,
                        xToken: response.headers.get('X-Token') || null,
                    });
                })
                .catch((error: any) => {
                    // if (useSpinner) useSpinner.close();
                    abortController.abort();
                    resolve(undefined);
                });
        });
    }

    public useStore(store: any): void {
        if (this.currentStore?.onLeaveStore) {
            this.currentStore.onLeaveStore();
        }

        if (store !== null) {
            this.currentStore = new store(this);
            this.currentStore.init();
        } else {
            this.currentStore = null;
        }
    }

    public getDialogNumberInput(options: {
        title: string;
        message?: string;
        label?: string;
        placeholder?: string;
        initialValue?: number | null;
        onConfirm?: (values: object) => Promise<MatenRequestResponse>;
    }): Promise<{ confirmed: boolean; response?: object; error?: string; values?: any }> {
        return dialogInputStore.openDialog(
            'number',
            options.title,
            options.message || '',
            options.label || '',
            options.placeholder || '',
            options.initialValue || null,
            options.onConfirm
        );
    }

    public getDialogStringInput(options: {
        title: string;
        message?: string;
        label?: string;
        placeholder?: string;
        initialValue?: string | null;
        onConfirm?: (values: object) => Promise<MatenRequestResponse>;
    }): Promise<{ confirmed: boolean; response?: object; error?: string; values?: any }> {
        return dialogInputStore.openDialog(
            'text',
            options.title,
            options.message || '',
            options.label || '',
            options.placeholder || '',
            options.initialValue || null,
            options.onConfirm
        );
    }

    public getDialogMultiInput(options: {
        title: string;
        fields: DialogInputField[];
        message?: string;
        initialValue?: Record<string, string | number | null>;
        onConfirm?: (values: object) => Promise<MatenRequestResponse>;
    }): Promise<{ confirmed: boolean; response?: object; error?: string; values?: any }> {
        return dialogInputStore.openMultiFieldDialog(
            options.title,
            options.fields,
            options.message,
            options.initialValue,
            options.onConfirm
        );
    }

    public getDialogDelete(options: {
        title: string;
        message: string;
        searchWord: string;
        onConfirm?: () => Promise<MatenRequestResponse>;
    }): Promise<{ confirmed: boolean; response?: object; error?: string }> {
        return dialogInputStore.openDeleteDialog(
            options.title,
            options.message,
            options.searchWord,
            options.onConfirm
        );
    }

    public getDialogWait(options: {
        title: string;
        message: string;
        onConfirm?: () => Promise<MatenRequestResponse>;
    }): Promise<{ confirmed: boolean; response?: object; error?: string }> {
        return dialogInputStore.openWaitDialog(options.title, options.message, options.onConfirm);
    }

    public getDialogConfirm(options: {
        title: string;
        message: string;
        onConfirm?: () => Promise<MatenRequestResponse>;
    }): Promise<{ confirmed: boolean; response?: object; error?: string }> {
        return dialogInputStore.openConfirmDialog(
            options.title,
            options.message,
            options.onConfirm
        );
    }

    public openDialog(store: any, config: any = {}): void {
        this.currentDialog = new store(this);
        this.currentDialog.init(config);
    }

    public closeDialog(event: any = null): void {
        const dialogOnLeave: string = this.currentDialog.id;
        this.currentDialog = null;
        if (this.currentStore?.onDialogLeave) {
            this.currentStore.onDialogLeave(dialogOnLeave, event);
        }
    }

    public showSuccessToast(message: string): void {
        if (this.toastCb) {
            this.toastCb({
                severity: 'success',
                summary: 'Erfolgreich',
                life: 3000,
                message,
            });
        }
    }

    public showErrorToast(message: string): void {
        if (this.toastCb) {
            this.toastCb({
                severity: 'error',
                summary: 'Fehler',
                life: 3000,
                message,
            });
        }
    }

    public registerToastCallback(cb: any): void {
        this.toastCb = cb;
    }

    get currentTabId(): string {
        return this.user?.tabId || this.tabId || '';
    }

    get currentDeviceId(): string {
        return this.deviceId || '';
    }

    get menuData(): any {
        return this.appConfig?.menu || [];
    }

    get filteredMenuData(): any {
        return this.menuData.filter(
            (item: any) => !item.permission || this.user?.hasRight(item.permission)
        );
    }

    public findMenuItemByKey(key: string): any {
        return this.menuData.find((item: any) => item.key === key);
    }
}

let AppSdk: ApplicationSdk | undefined = undefined;

const getSdk = () => {
    if (!AppSdk) {
        AppSdk = new ApplicationSdk();
        AppSdk.init();
    }

    return AppSdk;
};

export default getSdk;
export { ApplicationSdk };
